import { useEffect, useRef, useState } from 'react';
import {
  checkCampaignFeasibility,
  type CampaignFeasibilityResponse,
} from './feasibilityService';
import type { UseCampaignFeasibilityResult } from './useCampaignFeasibility';

const DEBOUNCE_MS = 350;
const SPINNER_DELAY_MS = 200;

interface UseExtendFeasibilityInput {
  // Identity of the submission being extended — passed as `excludeSubmissionId`
  // so its own line item isn't double-counted as competing inventory.
  submissionId: string;
  productId: string | undefined;
  // Original campaign start date — passed so the backend's monthly-period
  // anchoring matches the existing campaign. The feasibility service builds
  // periods over startDate..newEndDate, then filters to those at or after
  // extendFromDate (the day after currentEndDate).
  startDate: string | undefined;
  // Current end date of the campaign before extension. The first new day is
  // currentEndDate + 1, which we pass as `extendFromDate` to scope the check
  // to only the new flights.
  currentEndDate: string | undefined;
  newEndDate: string | undefined;
  // Additional budget being added by this extension (NOT original + additional).
  // The backend allocates this only across the new periods.
  additionalBudget: number | undefined;
  targetingMode: 'default' | 'custom' | undefined;
  geoTargetZips?: string[];
  // CTV-474: DMA + state geo modes now route through the same geo-aware path.
  geoTargetDMAs?: string[];
  geoTargetStates?: string[];
  // 'upfront' → single-flight feasibility against the modified window.
  // 'monthly' → per-period feasibility across the extended monthly schedule.
  // Defaults to 'upfront' for back-compat with the Phase-E call sites.
  paymentPlan?: 'upfront' | 'monthly';
}

function addOneDayIso(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  const yy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * CTV-473: feasibility hook for the Extend Campaign UX. Mirrors
 * useCampaignFeasibility's debounce / abort / spinner-defer behaviour, but
 * takes its inputs as props instead of via form context (ExtendPanel uses
 * local React state, not RHF). Posts to the same `/inventory/campaign-
 * feasibility` endpoint with `excludeSubmissionId` so the submission's own
 * line item isn't counted as competing inventory.
 */
export function useExtendFeasibility(input: UseExtendFeasibilityInput): UseCampaignFeasibilityResult {
  const {
    submissionId,
    productId,
    startDate,
    currentEndDate,
    newEndDate,
    additionalBudget,
    targetingMode,
    geoTargetZips,
    geoTargetDMAs,
    geoTargetStates,
    paymentPlan = 'upfront',
  } = input;

  const [verdict, setVerdict] = useState<CampaignFeasibilityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spinnerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const inputsKey = JSON.stringify({
    submissionId,
    productId,
    startDate,
    currentEndDate,
    newEndDate,
    additionalBudget,
    targetingMode,
    paymentPlan,
    geoTargetZips: geoTargetZips ? [...geoTargetZips].sort() : null,
    geoTargetDMAs: geoTargetDMAs ? [...geoTargetDMAs].sort() : null,
    geoTargetStates: geoTargetStates ? [...geoTargetStates].sort() : null,
  });

  useEffect(() => {
    if (
      !productId ||
      !startDate ||
      !currentEndDate ||
      !newEndDate ||
      !additionalBudget ||
      additionalBudget <= 0 ||
      newEndDate <= currentEndDate
    ) {
      setVerdict(null);
      setLoading(false);
      setError(null);
      return;
    }

    // CTV-474: same as useCampaignFeasibility — drop the prior verdict on
    // input change so the warning card animates out before the new request.
    setVerdict(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const reqId = ++requestIdRef.current;

      if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
      spinnerTimerRef.current = setTimeout(() => {
        if (reqId === requestIdRef.current) setLoading(true);
      }, SPINNER_DELAY_MS);

      checkCampaignFeasibility(
        {
          productId,
          startDate,
          endDate: newEndDate,
          budget: additionalBudget,
          paymentPlan,
          targetingMode: targetingMode ?? 'default',
          geoTargetZips,
          geoTargetDMAs,
          geoTargetStates,
          excludeSubmissionId: submissionId,
          extendFromDate: addOneDayIso(currentEndDate),
        },
        ctrl.signal,
      )
        .then((data) => {
          if (reqId !== requestIdRef.current) return;
          setVerdict(data);
          setError(null);
        })
        .catch((e) => {
          if (reqId !== requestIdRef.current) return;
          const name = (e as { name?: string })?.name;
          if (name === 'AbortError' || name === 'CanceledError') return;
          setError('We could not check inventory just now. Try again in a moment.');
          setVerdict(null);
        })
        .finally(() => {
          if (spinnerTimerRef.current) {
            clearTimeout(spinnerTimerRef.current);
            spinnerTimerRef.current = null;
          }
          if (reqId === requestIdRef.current) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // `inputsKey` is a JSON-serialized digest of every input the effect
    // reads. Watching the string lets us debounce on value-equality rather
    // than reference-identity, but ESLint can't see through JSON.stringify
    // so it flags the inner reads as "missing deps". The same pattern + the
    // same suppression is used in the sibling useCampaignFeasibility hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsKey]);

  return { verdict, loading, error };
}

